import apiClient from './api';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

let razorpayLoaded = false;

export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (razorpayLoaded && window.Razorpay) {
      resolve(true);
      return;
    }
    const existing = document.getElementById('razorpay-checkout-script');
    if (existing) {
      existing.addEventListener('load', () => { razorpayLoaded = true; resolve(true); });
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-script';
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => { razorpayLoaded = true; resolve(true); };
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export const initiatePayment = async ({
  userId,
  userType,
  tier,
  billingCycle,
  addons = [],
  userName,
  userEmail,
  userPhone,
  onSuccess,
  onFailure,
  onDismiss,
}) => {
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    throw new Error('Failed to load Razorpay. Please check your internet connection.');
  }

  // Create order on backend
  const { data } = await apiClient.post('/payments/create-order', {
    user_id: userId,
    user_type: userType,
    tier: tier,
    billing_cycle: billingCycle,
    addons: addons,
  });

  if (data.is_mock) {
    // In mock mode, process payment directly
    try {
      const verifyRes = await apiClient.post(`/payments/mock-payment/${data.order_id}`);
      if (onSuccess) onSuccess(verifyRes.data);
      return verifyRes.data;
    } catch (err) {
      if (onFailure) onFailure({ error: err.message });
      throw err;
    }
  }

  // Open Razorpay Checkout
  return new Promise((resolve, reject) => {
    const options = {
      key: data.razorpay_key,
      amount: data.amount_in_paise,
      currency: data.currency,
      name: 'ZenStudios',
      description: `${data.tier_name} - ${billingCycle.replace('_', ' ')} plan`,
      order_id: data.razorpay_order_id,
      prefill: {
        name: userName || '',
        email: userEmail || '',
        contact: userPhone || '',
      },
      theme: {
        color: '#16a34a',
        backdrop_color: 'rgba(0,0,0,0.6)',
      },
      handler: async function (response) {
        try {
          const verifyRes = await apiClient.post('/payments/verify', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            order_id: data.order_id,
          });
          if (onSuccess) onSuccess(verifyRes.data);
          resolve(verifyRes.data);
        } catch (err) {
          if (onFailure) onFailure({ error: 'Payment verification failed. Please contact support with order: ' + data.order_id });
          reject(err);
        }
      },
      modal: {
        ondismiss: function () {
          if (onDismiss) onDismiss();
          reject(new Error('Payment cancelled by user'));
        },
        confirm_close: true,
        escape: true,
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response) {
      const errorCode = response.error?.code || 'UNKNOWN';
      const errorDesc = response.error?.description || 'Payment failed';
      if (onFailure) onFailure({ error: `${errorDesc} (${errorCode}). Order: ${data.order_id}` });
      reject(new Error(errorDesc));
    });
    rzp.open();
  });
};

export const initiateServicePayment = async ({
  amount,
  currency = 'INR',
  description,
  userName,
  userEmail,
  userPhone,
  metadata = {},
  onSuccess,
  onFailure,
  onDismiss,
}) => {
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    throw new Error('Failed to load Razorpay. Please check your internet connection.');
  }

  // Create generic service order on backend
  const { data } = await apiClient.post('/payments/create-service-order', {
    amount,
    currency,
    description,
    metadata,
    customer_name: userName,
    customer_email: userEmail,
    customer_phone: userPhone,
  });

  if (data.is_mock) {
    try {
      const verifyRes = await apiClient.post(`/payments/mock-service-payment/${data.order_id}`);
      if (onSuccess) onSuccess(verifyRes.data);
      return verifyRes.data;
    } catch (err) {
      if (onFailure) onFailure({ error: err.message });
      throw err;
    }
  }

  return new Promise((resolve, reject) => {
    const options = {
      key: data.razorpay_key,
      amount: data.amount_in_paise,
      currency: data.currency,
      name: 'ZenStudios',
      description: description,
      order_id: data.razorpay_order_id,
      prefill: {
        name: userName || '',
        email: userEmail || '',
        contact: userPhone || '',
      },
      theme: {
        color: '#16a34a',
        backdrop_color: 'rgba(0,0,0,0.6)',
      },
      handler: async function (response) {
        try {
          const verifyRes = await apiClient.post('/payments/verify-service', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            order_id: data.order_id,
          });
          if (onSuccess) onSuccess(verifyRes.data);
          resolve(verifyRes.data);
        } catch (err) {
          if (onFailure) onFailure({ error: 'Payment verification failed. Contact support: ' + data.order_id });
          reject(err);
        }
      },
      modal: {
        ondismiss: function () {
          if (onDismiss) onDismiss();
          reject(new Error('Payment cancelled by user'));
        },
        confirm_close: true,
        escape: true,
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response) {
      const errorCode = response.error?.code || 'UNKNOWN';
      const errorDesc = response.error?.description || 'Payment failed';
      if (onFailure) onFailure({ error: `${errorDesc} (${errorCode}). Order: ${data.order_id}` });
      reject(new Error(errorDesc));
    });
    rzp.open();
  });
};
